import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  InputAdornment, FormControlLabel, Checkbox
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import CloseIcon from '@mui/icons-material/Close';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import PaddingIcon from '@mui/icons-material/Padding';
import FontDownloadIcon from '@mui/icons-material/FontDownload';
import OpacityIcon from '@mui/icons-material/Opacity';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { usePxToVw } from '../hooks/usePxToVw';
import useFontFamilies from '../hooks/useFontFamilies';
import IconToggleField from './IconToggleField';

// Import icons for toggle
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { FaPowerOff } from "react-icons/fa";
import { LuPowerOff } from "react-icons/lu";
import { FaToggleOn } from "react-icons/fa6";
import { FaToggleOff } from "react-icons/fa6";
import { MdClosedCaption } from "react-icons/md";
import { MdClosedCaptionOff } from "react-icons/md";
import { useToast } from 'react-toastify';
import UseToast from './UseToast';

// Styled components
const CaptionModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: theme.shadows[24],
  padding: theme.spacing(4),
  width: '90%',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const CaptionFieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));

const CaptionPreview = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  color: '#fff',
  borderRadius: '8px',
  marginTop: theme.spacing(2),
  minHeight: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  position: 'relative',
  overflow: 'visible',
}));

const PreviewVideoMock = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  opacity: 0.7,
}));

const PreviewText = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  maxWidth: '80%',
  transition: 'all 0.3s ease',
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const ShadowInputsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(4),
  marginTop: theme.spacing(1),
}));

const IconSelectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  marginTop: theme.spacing(2),
  backgroundColor: '#f8f9fa',
}));

const IconGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const IconOption = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  border: `2px solid ${selected ? theme.palette.primary.main : '#e0e0e0'}`,
  borderRadius: '8px',
  cursor: 'pointer',
  backgroundColor: selected ? '#f0f7ff' : 'white',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.light,
    backgroundColor: '#f8fdff',
  },
}));

const IconLabel = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: '0.75rem',
  textAlign: 'center',
}));

const IconPreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: '8px',
  minHeight: '80px',
  backgroundColor: '#f9f9f9',
}));

// Icon options with labels
const ICON_OPTIONS = [
  {
    name: 'power',
    label: 'Power Button ',
    onIcon: <FaPowerOff />,
    offIcon: <LuPowerOff />,
    color: '#4CAF50',
  },
  {
    name: 'toggle',
    label: 'Toggle Switch',
    onIcon: <FaToggleOn />,
    offIcon: <FaToggleOff />,
    color: '#2196F3',
  },
  {
    name: 'caption',
    label: 'Caption Toggle',
    onIcon: <MdClosedCaption />,
    offIcon: <MdClosedCaptionOff />,
    color: '#FF9800',
  },
];

const CaptionSettingsModal = ({
  open,
  onClose,
  userId,
  brandColor,
  setHasCaptionSettings,
}) => {
  const fontOptions = useFontFamilies('menuitem');
  const { pxToVw } = usePxToVw();
  const { showMsgSuccessToast, showErrorToast } = UseToast();
  const [captionSettings, setCaptionSettings] = useState({
    textColor: '#ffffff',
    bgColor: '#000000',
    backgroundOpacity: 60,
    fontSize: 50,
    padding: 30,
    fontWeight: 600,
    bottom: 40,
    textShadowH: '2',
    textShadowV: '2',
    textShadowBlur: '4',
    textShadowColor: '#000000',
    lineHeight: 50,
    letterSpacing: 0,
    fontFamily: 'Arial',
    transition: 2,
    buttonTop: 50,
    buttonLeft: 50,
    // New icon toggle settings
    iconType: 'toggle',
    iconSize: 50,
    iconOnColor: '#4CAF50',
    iconOffColor: '#757575',

  });

  // Get background color with opacity
  const getBackgroundColorWithOpacity = () => {
    const { bgColor, backgroundOpacity } = captionSettings;
    const opacity = backgroundOpacity / 100;

    if (bgColor.startsWith('rgba')) {
      const matches = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)/);
      if (matches) {
        const r = matches[1];
        const g = matches[2];
        const b = matches[3];
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
    } else if (bgColor.startsWith('rgb')) {
      const matches = bgColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (matches) {
        return `rgba(${matches[1]}, ${matches[2]}, ${matches[3]}, ${opacity})`;
      }
    } else if (bgColor.startsWith('#')) {
      const hex = bgColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    return `rgba(0, 0, 0, ${opacity})`;
  };

  // Get text shadow value
  const getTextShadowValue = () => {
    const { textShadowH, textShadowV, textShadowBlur, textShadowColor } = captionSettings;
    return `${textShadowH}px ${textShadowV}px ${textShadowBlur}px ${textShadowColor}`;
  };

  // Get preview style
  const getPreviewStyle = () => {
    const backgroundColor = getBackgroundColorWithOpacity();
    const textShadow = getTextShadowValue();

    return {
      color: captionSettings.textColor,
      backgroundColor: backgroundColor,
      fontSize: `${pxToVw(captionSettings.fontSize)}`,
      padding: `${pxToVw(captionSettings.padding)}`,
      fontWeight: captionSettings.fontWeight,
      bottom: `${pxToVw(captionSettings.bottom)}`,
      textShadow: textShadow,
      lineHeight: `${pxToVw(captionSettings.lineHeight)}`,
      letterSpacing: `${pxToVw(captionSettings.letterSpacing)}`,
      fontFamily: captionSettings.fontFamily,
      transition: `${captionSettings.transition}s`,
      borderRadius: '4px',
      display: 'inline-block',
      maxWidth: '100%',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      position: 'relative',
      marginBottom: `${pxToVw(captionSettings.bottom)}`,
      top: '0'
    };
  };

  // Handle caption settings change
  const handleCaptionChange = (field, value) => {
    setCaptionSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle text shadow changes
  const handleTextShadowChange = (field, value) => {
    setCaptionSettings(prev => ({
      ...prev,
      [`textShadow${field}`]: value,
    }));
  };

  // Handle icon type selection
  const handleIconTypeSelect = (iconType) => {
    const selectedIcon = ICON_OPTIONS.find(icon => icon.name === iconType);
    setCaptionSettings(prev => ({
      ...prev,
      iconType,
      iconOnColor: selectedIcon?.color || '#4CAF50',
    }));
  };

  // Get selected icon configuration
  const getSelectedIconConfig = () => {
    return ICON_OPTIONS.find(icon => icon.name === captionSettings.iconType) || ICON_OPTIONS[0];
  };

  const handleSaveCaptionSettings = () => {
    // Create text shadow string
    const textShadow = `${captionSettings.textShadowH}px ${captionSettings.textShadowV}px ${captionSettings.textShadowBlur}px ${captionSettings.textShadowColor}`;

    // Create background color with opacity
    const getFormattedBgColor = () => {
      const opacity = captionSettings.backgroundOpacity / 100;

      if (captionSettings.bgColor.startsWith('rgb')) {
        const matches = captionSettings.bgColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (matches) {
          return `rgba(${matches[1]}, ${matches[2]}, ${matches[3]}, ${opacity})`;
        }
      } else if (captionSettings.bgColor.startsWith('#')) {
        const hex = captionSettings.bgColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }

      return `rgba(0, 0, 0, ${opacity})`;
    };

    const payload = {
      userId,
      textColor: captionSettings.textColor,
      bgColor: getFormattedBgColor(),
      fontSize: captionSettings.fontSize,
      padding: captionSettings.padding,
      fontWeight: captionSettings.fontWeight,
      bottom: captionSettings.bottom,
      lineHeight: captionSettings.lineHeight,
      letterSpacing: captionSettings.letterSpacing,
      fontFamily: captionSettings.fontFamily,
      transition: captionSettings.transition,
      status: true,
      textShadowH: captionSettings.textShadowH,
      textShadowV: captionSettings.textShadowV,
      textShadowBlur: captionSettings.textShadowBlur,
      textShadowColor: captionSettings.textShadowColor,
      buttonTop: captionSettings.buttonTop,
      buttonLeft: captionSettings.buttonLeft,
      // Icon toggle settings
      iconType: captionSettings.iconType,
      iconSize: captionSettings.iconSize,
      iconOnColor: captionSettings.iconOnColor,
      iconOffColor: captionSettings.iconOffColor,
      // Text shadow (combined)
      textShadow: textShadow,
    };

    console.log('Saving caption settings:', payload);

    axios
      .post(`/api/captions/add-caption`, payload)
      .then(response => {
        showMsgSuccessToast('Caption settings saved successfully!');
        setHasCaptionSettings(true);
        onClose();
      })
      .catch(error => {
        console.error('Error saving caption settings:', error);
        showErrorToast('Failed to save caption settings');
      });
  };

  useEffect(() => {
    if (userId) {
      axios
        .get(`/api/captions/get-caption/${userId}`)
        .then(response => {
          if (response.data.status === true) {
            const captionData = response.data;
            setHasCaptionSettings(true);

            // Parse background opacity from existing bgColor
            let backgroundOpacity = 60; // default
            let bgColor = '#000000'; // default

            if (captionData.bgColor) {
              if (captionData.bgColor.startsWith('rgba')) {
                // Extract opacity from rgba
                const matches = captionData.bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)/);
                if (matches && matches[4]) {
                  backgroundOpacity = Math.round(parseFloat(matches[4]) * 100);
                  // Get just the RGB part
                  bgColor = `rgb(${matches[1]}, ${matches[2]}, ${matches[3]})`;
                }
              } else {
                bgColor = captionData.bgColor;
              }
            }

            // Parse text shadow properties
            let textShadowH = '2';
            let textShadowV = '2';
            let textShadowBlur = '4';
            let textShadowColor = '#000000';

            if (captionData.textShadow) {
              const shadowParts = captionData.textShadow.split(' ');
              if (shadowParts.length >= 4) {
                textShadowH = shadowParts[0].replace('px', '');
                textShadowV = shadowParts[1].replace('px', '');
                textShadowBlur = shadowParts[2].replace('px', '');
                textShadowColor = shadowParts.slice(3).join(' ');
              }
            }

            // Parse numeric values from strings
            const parseNumeric = (value, defaultValue) => {
              if (typeof value === 'string') {
                const num = parseFloat(value);
                return isNaN(num) ? defaultValue : num.toString();
              }
              return value;
            };

            setCaptionSettings({
              textColor: captionData.textColor || '#ffffff',
              bgColor: bgColor,
              backgroundOpacity: backgroundOpacity,
              fontSize: parseNumeric(captionData.fontSize, 50),
              padding: parseNumeric(captionData.padding, 30),
              fontWeight: captionData.fontWeight || 600,
              bottom: parseNumeric(captionData.bottom, 40),

              // Text shadow properties
              textShadowH: textShadowH,
              textShadowV: textShadowV,
              textShadowBlur: textShadowBlur,
              textShadowColor: textShadowColor,

              lineHeight: captionData.lineHeight || 50,
              letterSpacing: parseNumeric(captionData.letterSpacing, 0),
              fontFamily: captionData.fontFamily || 'Arial',
              transition: parseNumeric(captionData.transition, 2),
              buttonTop: captionData.buttonTop,
              buttonLeft: captionData.buttonLeft,

              // Icon toggle settings
              iconType: captionData.iconType,
              iconSize: captionData.iconSize,
              iconOnColor: captionData.iconOnColor,
              iconOffColor: captionData.iconOffColor,

            });
          }
        })
        .catch(error => {
          console.log('No caption settings found');
          setHasCaptionSettings(false);
        });
    }
  }, [userId]);



  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="caption-settings-modal"
      aria-describedby="caption-styling-configuration"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <CaptionModalContent>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Video Caption Styling
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
          Customize how captions appear on your videos. Changes update in real-time below.
        </Typography>

        <Grid container spacing={3}>
          {/* Left Column - Text Properties */}
          <Grid item xs={12} md={6}>
            {/* Text Color */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorLensIcon fontSize="small" /> Text Color
              </Typography>
              <MuiColorInput
                format="hex"
                value={captionSettings.textColor}
                onChange={(value) => handleCaptionChange('textColor', value)}
                fullWidth
                size="small"
              />
            </CaptionFieldContainer>

            {/* Font Size */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormatSizeIcon fontSize="small" /> Font Size
              </Typography>
              <TextField
                type='number'
                value={captionSettings.fontSize}
                onChange={(e) => handleCaptionChange('fontSize', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 22px, 1.5rem"
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
              />
            </CaptionFieldContainer>

            {/* Padding */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaddingIcon fontSize="small" /> Padding
              </Typography>
              <TextField
                type='number'
                value={captionSettings.padding}
                onChange={(e) => handleCaptionChange('padding', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 8"

                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
              />
            </CaptionFieldContainer>


            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom>
                Letter Spacing
              </Typography>
              <TextField
                value={captionSettings.letterSpacing}
                onChange={(e) => handleCaptionChange('letterSpacing', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 0.5"
                type='number'
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
              />
            </CaptionFieldContainer>

            {/* Font Weight */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom>
                Font Weight
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={captionSettings.fontWeight}
                  onChange={(e) => handleCaptionChange('fontWeight', e.target.value)}
                >
                  <MenuItem value="100">Thin (100)</MenuItem>
                  <MenuItem value="200">Extra Light (200)</MenuItem>
                  <MenuItem value="300">Light (300)</MenuItem>
                  <MenuItem value="400">Regular (400)</MenuItem>
                  <MenuItem value="500">Medium (500)</MenuItem>
                  <MenuItem value="600">Semi Bold (600)</MenuItem>
                  <MenuItem value="700">Bold (700)</MenuItem>
                  <MenuItem value="800">Extra Bold (800)</MenuItem>
                  <MenuItem value="900">Black (900)</MenuItem>
                </Select>
              </FormControl>
            </CaptionFieldContainer>


          </Grid>

          {/* Right Column - Background & Shadow Properties */}
          <Grid item xs={12} md={6}>
            {/* Background Color */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorLensIcon fontSize="small" /> Background Color
              </Typography>
              <MuiColorInput
                format="hex"
                value={captionSettings.bgColor.startsWith('#') ? captionSettings.bgColor : '#000000'}
                onChange={(value) => handleCaptionChange('bgColor', value)}
                fullWidth
                size="small"
              />
            </CaptionFieldContainer>

            {/* Background Opacity */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '15px' }}>
                <OpacityIcon fontSize="small" /> Background Opacity
              </Typography>
              <SliderContainer>
                <Slider
                  value={captionSettings.backgroundOpacity}
                  onChange={(e, value) => handleCaptionChange('backgroundOpacity', value)}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2" sx={{ minWidth: '50px', textAlign: 'right' }}>
                  {captionSettings.backgroundOpacity}%
                </Typography>
              </SliderContainer>
            </CaptionFieldContainer>

            {/* Bottom Position */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom>
                Position from Bottom
              </Typography>
              <TextField
                value={captionSettings.bottom}
                onChange={(e) => handleCaptionChange('bottom', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 40"
                type='number'
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
              />
              {/* <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Distance from bottom of video
              </Typography> */}
            </CaptionFieldContainer>

            {/* Transition Duration */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom>
                Transition Duration
              </Typography>
              <TextField
                value={captionSettings.transition}
                onChange={(e) => handleCaptionChange('transition', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 2"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                }}
              />
              {/* <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Animation duration for caption changes
              </Typography> */}
            </CaptionFieldContainer>

            {/* Line Height */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom>
                Line Height
              </Typography>
              <TextField
                value={captionSettings.lineHeight}
                onChange={(e) => handleCaptionChange('lineHeight', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g., 1.4"
                type='number'
                InputProps={{
                  endAdornment: <InputAdornment position="end">px</InputAdornment>,
                }}
              />
            </CaptionFieldContainer>

          </Grid>

          <Grid item xs={12}>
            {/* Font Family */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontDownloadIcon fontSize="small" /> Font Family
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={captionSettings.fontFamily}
                  onChange={(e) => handleCaptionChange('fontFamily', e.target.value)}
                >
                  {fontOptions}
                </Select>
              </FormControl>
            </CaptionFieldContainer>
          </Grid>

          <Grid item xs={12}>
            {/* Text Shadow */}
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '2px' }}>
                <ColorLensIcon fontSize="small" /> Text Shadow
              </Typography>

              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block', fontSize: '15px' }}>
                Format: H-shadow V-shadow Blur Color
              </Typography>

              <ShadowInputsContainer>
                <TextField
                  label="Horizontal"
                  value={captionSettings.textShadowH}
                  onChange={(e) => handleTextShadowChange('H', e.target.value)}
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  }}
                />

                <TextField
                  label="Vertical"
                  value={captionSettings.textShadowV}
                  onChange={(e) => handleTextShadowChange('V', e.target.value)}
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  }}
                />

                <TextField
                  label="Blur Radius"
                  value={captionSettings.textShadowBlur}
                  onChange={(e) => handleTextShadowChange('Blur', e.target.value)}
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                  }}
                />

                <MuiColorInput
                  label="Shadow Color"
                  format="hex"
                  value={captionSettings.textShadowColor}
                  onChange={(value) => handleTextShadowChange('Color', value)}
                  size="small"
                />
              </ShadowInputsContainer>

              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', fontSize: '15px' }}>
                Current: {getTextShadowValue()}
              </Typography>
            </CaptionFieldContainer>
          </Grid>


          {/* Icon Toggle Settings */}
          <Grid item xs={12}>
            <CaptionFieldContainer>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleOnIcon /> Icon Toggle Settings
              </Typography>

              <>
                {/* Icon Type Selection */}
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Select Icon Type
                </Typography>
                <IconGrid>
                  {ICON_OPTIONS.map((icon) => (
                    <IconOption
                      key={icon.name}
                      selected={captionSettings.iconType === icon.name}
                      onClick={() => handleIconTypeSelect(icon.name)}
                    >
                      <Box sx={{
                        fontSize: '35px',
                        color: icon.color,
                      }}>
                        {icon.onIcon}
                      </Box>
                      <IconLabel>{icon.label}</IconLabel>
                    </IconOption>
                  ))}
                </IconGrid>

                {/* Icon Size */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Icon Size
                  </Typography>
                  <TextField
                    value={captionSettings.iconSize}
                    onChange={(e) => handleCaptionChange('iconSize', e.target.value)}
                    fullWidth
                    size="small"
                    type="number"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">px</InputAdornment>,
                    }}
                  />
                </Box>

                {/* Icon Colors */}
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      On State Color
                    </Typography>
                    <MuiColorInput
                      format="hex"
                      value={captionSettings.iconOnColor}
                      onChange={(value) => handleCaptionChange('iconOnColor', value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      Off State Color
                    </Typography>
                    <MuiColorInput
                      format="hex"
                      value={captionSettings.iconOffColor}
                      onChange={(value) => handleCaptionChange('iconOffColor', value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>

                {/* Button Position */}
                <Grid container spacing={2} sx={{ mt: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      Button Top Position
                    </Typography>
                    <TextField
                      value={captionSettings.buttonTop}
                      onChange={(e) => handleCaptionChange('buttonTop', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />

                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      Button Left Position
                    </Typography>
                    <TextField
                      value={captionSettings.buttonLeft}
                      onChange={(e) => handleCaptionChange('buttonLeft', e.target.value)}
                      fullWidth
                      size="small"
                      type="number"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Icon Preview */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Icon Preview
                  </Typography>
                  <IconPreview>
                    <Box sx={{
                      display: 'flex',
                      gap: 3,
                      alignItems: 'center',
                      fontSize: `${pxToVw(captionSettings.iconSize || '30px')}`,
                    }}>
                      <Box sx={{
                        color: captionSettings.iconOnColor,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        {getSelectedIconConfig().onIcon}
                        <Typography variant="caption" color="textSecondary">
                          ON State
                        </Typography>
                      </Box>
                      <Box sx={{
                        color: captionSettings.iconOffColor,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        {getSelectedIconConfig().offIcon}
                        <Typography variant="caption" color="textSecondary">
                          OFF State
                        </Typography>
                      </Box>
                    </Box>
                  </IconPreview>
                </Box>
              </>

            </CaptionFieldContainer>
          </Grid>

          {/* Preview Section */}
          <Grid item xs={12}>
            <CaptionFieldContainer>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityIcon fontSize="small" /> Live Preview
              </Typography>

              <CaptionPreview>
                <PreviewVideoMock />
                <PreviewText style={getPreviewStyle()}>
                  This is how your captions will appear on video
                </PreviewText>
              </CaptionPreview>
            </CaptionFieldContainer>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCaptionSettings}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              backgroundColor: brandColor,
              '&:hover': { backgroundColor: brandColor },
            }}
          >
            Save Caption Settings
          </Button>
        </Box>
      </CaptionModalContent>
    </Modal>
  );
};

export default CaptionSettingsModal;
